import type { ChatMentionsMenuItemType } from '@terra/ui-design-system';
import type { SkillMentionData } from '@terra/shared-dto-chat-types';
import type { SkillDraft } from '../store/skillDraftsStore';
import { BookOpen01Icon, TextSelectIcon } from '@terra/ui-icons';
import { createElement } from 'react';

export function formatSkillMentionRawText({
  mode,
  skillId,
  skillDraftId,
  draft,
  title,
  untrustedDataNotice,
}: {
  mode: 'create' | 'edit';
  skillId?: string;
  skillDraftId: string;
  draft: SkillDraft;
  title: string;
  untrustedDataNotice: string;
}) {
  const draftPayload = {
    mode,
    skillId,
    skillDraftId,
    slug: draft.slug || null,
    name: draft.name || null,
    description: draft.description || null,
    body: draft.body || null,
  };

  return [
    title,
    untrustedDataNotice,
    '',
    JSON.stringify(draftPayload, null, 2),
  ].join('\n');
}

export function createSkillMention({
  skillDraftId,
  skillId,
  mode,
  label,
  fallbackLabel,
}: {
  skillDraftId: string;
  skillId?: string;
  mode: 'create' | 'edit';
  label?: string;
  fallbackLabel: string;
}): ChatMentionsMenuItemType {
  const mentionLabel = label || fallbackLabel;
  const skillData: SkillMentionData = {
    skillDraftId,
    skillId,
    mode,
  };

  return {
    id: `skill-${skillDraftId}`,
    label: mentionLabel,
    icon: createElement(BookOpen01Icon, { size: 12 }),
    type: 'resource',
    mentionEntity: {
      entityType: 'Skill',
      entityId: skillId ?? skillDraftId,
      label: mentionLabel,
      extraData: {
        skill: skillData,
        skillDraftId,
        skillId,
        mode,
      },
    },
  };
}

export function createSkillFieldContextMention({
  skillDraftId,
  skillId,
  mode,
  field,
  label,
  value,
  contextLabel,
  contextDisplayLabel,
  requestedAction,
  rawContextTitle,
  rawContextUntrustedDataNotice,
  requestId,
}: {
  skillDraftId: string;
  skillId?: string;
  mode: 'create' | 'edit';
  field: string;
  label: string;
  value: string;
  contextLabel: string;
  contextDisplayLabel: string;
  requestedAction: string;
  rawContextTitle: string;
  rawContextUntrustedDataNotice: string;
  requestId: number;
}): ChatMentionsMenuItemType {
  const mentionLabel = `${label} ${contextLabel}`;
  const rawText = [
    rawContextTitle,
    rawContextUntrustedDataNotice,
    '',
    JSON.stringify(
      {
        mode,
        skillId,
        skillDraftId,
        field,
        label,
        context: contextDisplayLabel,
        requestId,
        requestedAction,
        value,
      },
      null,
      2,
    ),
  ].join('\n');

  return {
    id: `skill-field-${skillDraftId}-${field}-${Date.now()}`,
    label: mentionLabel,
    icon: createElement(TextSelectIcon, { size: 12 }),
    type: 'resource',
    mentionEntity: {
      entityType: 'TestingInstruction',
      entityId: `${skillDraftId}:${field}:${Date.now()}`,
      label: mentionLabel,
      extraData: {
        testingInstruction: {
          title: mentionLabel,
          field,
          context: contextDisplayLabel,
          excerpt: value,
          rawText,
        },
      },
    },
  };
}
